/**
 * Browser test-chat — push-to-talk through the same voice-agent
 * pipeline that handles real phone calls (STT → LLM → tools → TTS),
 * but driven by the user's microphone + speakers instead of Twilio /
 * Telnyx. Saves telephony minutes while iterating on the prompt.
 *
 * The conversation history is kept locally in component state and
 * forwarded to the server on each turn so the LLM has continuity. The
 * system prompt is rebuilt server-side from the current PhoneSettings
 * on every turn — edits made in another tab are picked up immediately.
 */
import * as React from 'react';
import { Loader2, Mic, PhoneOff, RefreshCw, Sparkles, Square } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import {
  useVoiceTestTurn,
  type VoiceTestHistoryItem,
} from '../../../hooks/useVoiceTestTurn';
import { useStartLiveKitTestSession } from '../../../hooks/useLiveKit';
import { useLiveKitSession } from '../../../hooks/useLiveKitSession';
import { useSecretStatus } from '../../../hooks/useSecretStatus';
import { useToast } from '../../../components/ui/toast';
import { cn } from '../../../lib/utils';
import { SectionFrame } from './SectionFrame';

type TestMode = 'push-to-talk' | 'livekit';

interface UiMessage {
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: Array<{ name: string; args: unknown; result: unknown }>;
}

export function TestSection() {
  const [messages, setMessages] = React.useState<UiMessage[]>([]);
  const [recording, setRecording] = React.useState(false);
  const [permissionError, setPermissionError] = React.useState<string | null>(null);
  const [statusNote, setStatusNote] = React.useState<string | null>(null);

  const recorderRef = React.useRef<MediaRecorder | null>(null);
  const chunksRef = React.useRef<Blob[]>([]);
  const streamRef = React.useRef<MediaStream | null>(null);
  const audioElRef = React.useRef<HTMLAudioElement | null>(null);

  const turn = useVoiceTestTurn();

  // Stop any ongoing capture on unmount so we never leave the mic LED on.
  React.useEffect(
    () => () => {
      stopMediaTracks(streamRef.current);
    },
    [],
  );

  const history: VoiceTestHistoryItem[] = React.useMemo(
    () => messages.map((m) => ({ role: m.role, content: m.content })),
    [messages],
  );

  async function startRecording() {
    setPermissionError(null);
    setStatusNote(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;
      const mime = pickAudioMime();
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, {
          type: recorder.mimeType || 'audio/webm',
        });
        stopMediaTracks(streamRef.current);
        streamRef.current = null;
        await sendBlob(blob);
      };
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
    } catch (err) {
      const msg =
        (err as DOMException)?.name === 'NotAllowedError'
          ? 'Mikrofonzugriff wurde abgelehnt. Bitte erlaube den Browser-Zugriff und versuche es erneut.'
          : `Mikrofon konnte nicht gestartet werden: ${(err as Error).message}`;
      setPermissionError(msg);
    }
  }

  function stopRecording() {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch {
        /* ignore */
      }
    }
    setRecording(false);
  }

  async function sendBlob(blob: Blob) {
    if (blob.size < 1024) {
      setStatusNote('Kein Audio aufgenommen — Knopf länger gedrückt halten.');
      return;
    }
    const audioB64 = await blobToBase64(blob);
    try {
      const result = await turn.mutateAsync({
        audioB64,
        audioMime: blob.type || 'audio/webm',
        history,
      });
      const newMessages: UiMessage[] = [];
      if (result.transcript) {
        newMessages.push({ role: 'user', content: result.transcript });
      }
      if (result.reply_text || (result.tool_calls && result.tool_calls.length > 0)) {
        newMessages.push({
          role: 'assistant',
          content: result.reply_text || '',
          toolCalls: result.tool_calls,
        });
      }
      if (newMessages.length > 0) {
        setMessages((prev) => [...prev, ...newMessages]);
      }
      if (result.note) setStatusNote(result.note);
      else setStatusNote(null);

      if (result.audio_b64 && audioElRef.current) {
        const dataUrl = `data:${result.audio_mime || 'audio/mpeg'};base64,${result.audio_b64}`;
        audioElRef.current.src = dataUrl;
        try {
          await audioElRef.current.play();
        } catch {
          // Autoplay can be blocked on first gesture — fall back to user-click.
        }
      }
    } catch (err) {
      setStatusNote((err as Error).message || 'Fehler beim Senden des Audios.');
    }
  }

  function reset() {
    setMessages([]);
    setStatusNote(null);
    if (audioElRef.current) {
      try {
        audioElRef.current.pause();
        audioElRef.current.removeAttribute('src');
        audioElRef.current.load();
      } catch {
        /* ignore */
      }
    }
  }

  const [mode, setMode] = React.useState<TestMode>('push-to-talk');
  const secrets = useSecretStatus();
  const livekitReady = !!secrets.data?.livekit?.apiKey && !!secrets.data?.livekit?.apiSecret;

  return (
    <SectionFrame>
      <ModeSwitch mode={mode} setMode={setMode} livekitReady={livekitReady} />

      {mode === 'livekit' ? (
        <LiveKitTestCard />
      ) : (
      <Card>
        <CardHeader>
          <CardTitle>Pipeline-Test im Browser</CardTitle>
          <CardDescription>
            Drücke „Sprechen", halte gedrückt und sprich deine Anfrage. Die komplette
            Pipeline (Transkription, KI-Modell mit allen Tools, Sprachausgabe) läuft so wie
            bei einem echten Anruf — nur dass dein Mikrofon und die Lautsprecher die
            Telefonleitung ersetzen. Twilio-/Telnyx-Minuten werden nicht verbraucht; STT-,
            LLM- und TTS-Kosten fallen jedoch real an.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            {!recording ? (
              <Button
                size="lg"
                onClick={() => void startRecording()}
                disabled={turn.isPending}
              >
                {turn.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Antwortet …
                  </>
                ) : (
                  <>
                    <Mic className="mr-2 h-4 w-4" /> Sprechen
                  </>
                )}
              </Button>
            ) : (
              <Button size="lg" variant="destructive" onClick={stopRecording}>
                <Square className="mr-2 h-4 w-4" /> Stop &amp; senden
              </Button>
            )}
            <Button
              size="lg"
              variant="outline"
              onClick={reset}
              disabled={messages.length === 0 || turn.isPending || recording}
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Gespräch zurücksetzen
            </Button>
          </div>

          {permissionError && (
            <p className="text-sm text-destructive">{permissionError}</p>
          )}
          {statusNote && (
            <p className="text-sm text-muted-foreground">{statusNote}</p>
          )}

          <audio ref={audioElRef} controls className="w-full" />

          <Conversation messages={messages} />
        </CardContent>
      </Card>
      )}
    </SectionFrame>
  );
}

// ── Mode switch ────────────────────────────────────────────────────────

function ModeSwitch({
  mode,
  setMode,
  livekitReady,
}: {
  mode: TestMode;
  setMode: (m: TestMode) => void;
  livekitReady: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card p-2 text-sm">
      <span className="px-2 text-xs uppercase tracking-wider text-muted-foreground">
        Verbindung
      </span>
      <Button
        size="sm"
        variant={mode === 'push-to-talk' ? 'default' : 'ghost'}
        onClick={() => setMode('push-to-talk')}
        className="h-7"
      >
        <Mic className="mr-1 size-3.5" />
        Push-to-Talk
      </Button>
      <Button
        size="sm"
        variant={mode === 'livekit' ? 'default' : 'ghost'}
        onClick={() => setMode('livekit')}
        disabled={!livekitReady}
        className="h-7"
      >
        <Sparkles className="mr-1 size-3.5" />
        LiveKit (HD-Audio)
      </Button>
      {!livekitReady && (
        <span className="ml-1 text-[11px] text-muted-foreground">
          LK-Credentials fehlen — unter API-Schlüssel hinterlegen
        </span>
      )}
    </div>
  );
}

// ── LiveKit test card ──────────────────────────────────────────────────

function LiveKitTestCard() {
  const session = useLiveKitSession();
  const startSession = useStartLiveKitTestSession();
  const { toast } = useToast();

  async function onConnect() {
    try {
      const r = await startSession.mutateAsync();
      await session.connect({ token: r.token, url: r.url });
      toast({
        title: 'LiveKit-Sitzung aktiv',
        description: 'Sprich frei in dein Mikrofon — der Agent hört live mit.',
      });
    } catch (err) {
      toast({
        title: 'LiveKit-Sitzung fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  const busy = startSession.isPending || session.state === 'connecting';
  const stateLabel: Record<typeof session.state, string> = {
    idle: 'bereit',
    connecting: 'verbinde …',
    connected: 'verbunden — du sprichst live',
    reconnecting: 'reconnect …',
    disconnected: 'beendet',
    failed: 'Verbindung fehlgeschlagen',
  };
  const agentJoined = session.remoteParticipants.some((p) =>
    p.identity.startsWith('cp3-agent'),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>LiveKit-Test (HD-Audio)</CardTitle>
        <CardDescription>
          Vollduplex-Sprachverbindung über LiveKit-WebRTC — gleiche
          Pipeline (STT → LLM mit Tools → TTS), aber Opus 48 kHz statt
          μ-law 8 kHz. Du hörst und sprichst gleichzeitig, kein Drücken
          nötig.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {!session.isLive ? (
            <Button size="lg" onClick={onConnect} disabled={busy}>
              {busy ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <Mic className="mr-2 size-4" />
              )}
              Sitzung starten
            </Button>
          ) : (
            <Button size="lg" variant="destructive" onClick={() => void session.disconnect()}>
              <PhoneOff className="mr-2 size-4" />
              Sitzung beenden
            </Button>
          )}
          <div className="text-sm">
            Status:{' '}
            <span
              className={cn(
                'font-medium',
                session.state === 'connected' && 'text-emerald-300',
                session.state === 'failed' && 'text-destructive',
              )}
            >
              {stateLabel[session.state]}
            </span>
            {session.isLive && (
              <span className="ml-2 text-xs text-muted-foreground">
                {agentJoined ? '· Agent ist im Raum' : '· warte auf Agent …'}
              </span>
            )}
          </div>
        </div>
        {session.error && (
          <p className="text-sm text-destructive">{session.error}</p>
        )}
        <p className="text-xs text-muted-foreground">
          Hinweis: Während der Sitzung läuft dein Mikrofon kontinuierlich.
          Beende die Sitzung wenn du fertig bist — sonst zählen die
          LK-Agent-Minuten weiter.
        </p>
      </CardContent>
    </Card>
  );
}

function Conversation({ messages }: { messages: UiMessage[] }) {
  if (messages.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        Noch kein Gespräch. Drücke „Sprechen", um zu starten.
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {messages.map((m, i) => (
        <div
          key={i}
          className={
            'rounded-md border px-3 py-2 text-sm ' +
            (m.role === 'user'
              ? 'border-border bg-secondary/40'
              : 'border-primary/30 bg-primary/5')
          }
        >
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {m.role === 'user' ? 'Du' : 'Agent'}
          </div>
          {m.content && <div className="whitespace-pre-wrap">{m.content}</div>}
          {m.toolCalls && m.toolCalls.length > 0 && (
            <ToolCallList calls={m.toolCalls} />
          )}
        </div>
      ))}
    </div>
  );
}

function ToolCallList({
  calls,
}: {
  calls: Array<{ name: string; args: unknown; result: unknown }>;
}) {
  return (
    <div className="mt-2 flex flex-col gap-1">
      {calls.map((c, i) => (
        <details key={i} className="rounded-md bg-background/60 p-2 text-xs">
          <summary className="cursor-pointer font-mono">
            🛠 {c.name}
          </summary>
          <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all">
            {JSON.stringify({ args: c.args, result: c.result }, null, 2)}
          </pre>
        </details>
      ))}
    </div>
  );
}

function pickAudioMime(): string | null {
  // Prefer codecs supported by Deepgram / ElevenLabs. Safari only does
  // mp4/m4a, Chrome/Firefox prefer webm/opus.
  if (typeof MediaRecorder === 'undefined') return null;
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
  ];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c)) return c;
  }
  return null;
}

function stopMediaTracks(stream: MediaStream | null) {
  if (!stream) return;
  for (const t of stream.getTracks()) {
    try {
      t.stop();
    } catch {
      /* ignore */
    }
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const idx = result.indexOf('base64,');
      resolve(idx >= 0 ? result.slice(idx + 'base64,'.length) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
